// src/pages/parametres/Parametres.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Tabs, Tab, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';

const COLORS = {
  primary: '#295AA7',
  secondary: '#FDB52F',
  success: '#10b981',
  danger: '#ef4444'
};

const settingSections = [
  {
    title: 'Paramètres généraux',
    icon: 'ri-settings-3-line',
    path: '/parametres/generaux',
    description: 'Configuration générale de l\'application'
  },
  {
    title: 'Sauvegarde',
    icon: 'ri-database-2-line',
    path: '/parametres/sauvegarde',
    description: 'Gérer les sauvegardes et restaurations'
  },
  {
    title: 'Notifications',
    icon: 'ri-notification-3-line',
    path: '/parametres/notifications',
    description: 'Configurer les notifications'
  },
  {
    title: 'Sécurité',
    icon: 'ri-lock-line',
    path: '/parametres/securite',
    description: 'Paramètres de sécurité et permissions'
  },
  {
    title: 'Apparence',
    icon: 'ri-palette-line',
    path: '/parametres/apparence',
    description: 'Personnaliser l\'apparence'
  },
  {
    title: 'Utilisateurs',
    icon: 'ri-user-settings-line',
    path: '/parametres/utilisateurs',
    description: 'Gérer les utilisateurs et rôles'
  }
];

export default function Parametres() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    siteName: 'JosephsBusiness',
    siteUrl: 'https://josephsbusiness.com',
    emailContact: 'contact@josephsbusiness.com',
    language: 'fr',
    timezone: 'Africa/Libreville',
    dateFormat: 'dd/MM/yyyy',
    itemsPerPage: 20
  });

  const handleSave = async () => {
    setLoading(true);
    try {
      // Simuler une sauvegarde
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('Paramètres enregistrés avec succès');
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <Toaster />
      <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="container-fluid">
          {/* Fil d'Ariane */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex align-items-center gap-2">
                <Button 
                  variant="link" 
                  className="text-decoration-none p-0"
                  onClick={() => navigate(-1)}
                  style={{ color: COLORS.primary }}
                >
                  <i className="ri-arrow-left-line fs-5"></i>
                </Button>
                <span className="text-muted">/</span>
                <span className="fw-semibold" style={{ color: COLORS.primary }}>Paramètres</span>
              </div>
            </Col>
          </Row>

          {/* En-tête */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex align-items-center gap-3">
                <div className="header-icon-wrapper" style={{ 
                  width: '60px', 
                  height: '60px', 
                  borderRadius: '16px',
                  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="ri-settings-3-line" style={{ fontSize: '28px', color: 'white' }}></i>
                </div>
                <div>
                  <h4 className="fw-bold mb-1" style={{ color: COLORS.primary }}>Paramètres</h4>
                  <p className="text-muted small mb-0">
                    Gérez la configuration de votre application
                  </p>
                </div>
              </div>
            </Col>
          </Row>

          {/* Sections de paramètres */}
          <Row className="g-4 mb-5">
            {settingSections.map((section, index) => (
              <Col md={4} key={index}>
                <Card 
                  className="setting-card border-0 shadow-sm h-100"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(section.path)}
                >
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center gap-3">
                      <div className="setting-icon" style={{ 
                        width: '50px', 
                        height: '50px', 
                        borderRadius: '12px',
                        background: `${COLORS.primary}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className={section.icon} style={{ color: COLORS.primary, fontSize: '24px' }}></i>
                      </div>
                      <div>
                        <h6 className="fw-semibold mb-1">{section.title}</h6>
                        <p className="small text-muted mb-0">{section.description}</p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Paramètres généraux */}
          <Row>
            <Col md={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-0 pt-4">
                  <h5 className="fw-semibold mb-0" style={{ color: COLORS.primary }}>
                    <i className="ri-slider-line me-2"></i>
                    Configuration générale
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row className="g-4">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Nom du site</Form.Label>
                        <Form.Control
                          type="text"
                          value={settings.siteName}
                          onChange={(e) => setSettings({...settings, siteName: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>URL du site</Form.Label>
                        <Form.Control
                          type="url"
                          value={settings.siteUrl}
                          onChange={(e) => setSettings({...settings, siteUrl: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Email de contact</Form.Label>
                        <Form.Control
                          type="email"
                          value={settings.emailContact}
                          onChange={(e) => setSettings({...settings, emailContact: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Langue par défaut</Form.Label>
                        <Form.Select 
                          value={settings.language}
                          onChange={(e) => setSettings({...settings, language: e.target.value})}
                        >
                          <option value="fr">Français</option>
                          <option value="en">English</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Fuseau horaire</Form.Label>
                        <Form.Select 
                          value={settings.timezone}
                          onChange={(e) => setSettings({...settings, timezone: e.target.value})}
                        >
                          <option value="Africa/Libreville">Afrique/Libreville</option>
                          <option value="Africa/Douala">Afrique/Douala</option>
                          <option value="Africa/Lagos">Afrique/Lagos</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Format de date</Form.Label>
                        <Form.Select 
                          value={settings.dateFormat}
                          onChange={(e) => setSettings({...settings, dateFormat: e.target.value})}
                        >
                          <option value="dd/MM/yyyy">31/12/2024</option>
                          <option value="MM/dd/yyyy">12/31/2024</option>
                          <option value="yyyy-MM-dd">2024-12-31</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Éléments par page</Form.Label>
                        <Form.Control
                          type="number"
                          value={settings.itemsPerPage}
                          onChange={(e) => setSettings({...settings, itemsPerPage: parseInt(e.target.value)})}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end mt-4">
                    <Button 
                      onClick={handleSave}
                      disabled={loading}
                      style={{ background: COLORS.primary, border: 'none', borderRadius: '30px', padding: '0.8rem 2rem' }}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Enregistrement...
                        </>
                      ) : (
                        <>
                          <i className="ri-save-line me-2"></i>
                          Enregistrer les modifications
                        </>
                      )}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        <style>{`
          .setting-card {
            transition: all 0.3s ease;
          }
          .setting-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
          }
          .setting-icon {
            transition: all 0.3s ease;
          }
          .setting-card:hover .setting-icon {
            background: ${COLORS.primary} !important;
          }
          .setting-card:hover .setting-icon i {
            color: white !important;
          }
        `}</style>

        <Footer />
      </div>
    </>
  );
}